import React, { useState, useEffect, useContext, useRef } from "react";
import "./ProfileInformation.css";
import { TbMail, TbUser } from "react-icons/tb";
import EditableSettingsInput from "../components/EditableSettingsInput/EditableSettingsInput";
import SettingsButton from "../components/SettingsButton/SettingsButton";
import { useLanguage } from "../../../LanguageProvider";
import Services from "../../../services/Services";
import { GeneralContext } from "../../../GeneralProvider";
import { useToaster } from "../../../ToasterProvider";
import LanguageSelect from "../../../components/LanguageSelect/LanguageSelect";

export default function ProfileInformation() {
  const { getTranslation } = useLanguage();
  const { toastError, toastSuccess } = useToaster();
  const { userId, useToken } = useContext(GeneralContext);

  const [name, setName] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [avatar, setAvatar] = useState(null);

  const fileInputRef = useRef(null);

  const handleSaveName = (newName) => {
    const trimmedName = newName.trim();
    if (!trimmedName) {
      toastError(getTranslation("nameCannotBeEmpty"));
      return;
    }
    if (!/^[a-zA-ZçÇğĞüÜşŞıİöÖ\s]+$/.test(trimmedName)) {
      toastError(getTranslation("nameCannotContainSpecialCharacters"));
      return;
    }
    const updateData = { name: trimmedName };
    setName(trimmedName);
    Services.UpdateUserData(useToken, userId, updateData)
      .then((res) => {
        if (res.data.status) {
          toastSuccess(getTranslation("successMessage"));
          let userData;
          if (sessionStorage.getItem("user")) {
            userData = JSON.parse(sessionStorage.getItem("user"));
            userData.name = trimmedName;
            sessionStorage.setItem("user", JSON.stringify(userData));
          } else if (localStorage.getItem("user")) {
            userData = JSON.parse(localStorage.getItem("user"));
            userData.name = trimmedName;
            localStorage.setItem("user", JSON.stringify(userData));
          }
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch(() => {
        toastError(getTranslation("somethingWentWrong"));
      });
  };

  const handleSaveLastname = (newLastname) => {
    const trimmedLastname = newLastname.trim();
    if (!trimmedLastname) {
      toastError(getTranslation("lastnameCannotBeEmpty"));
      return;
    }
    if (!/^[a-zA-ZçÇğĞüÜşŞıİöÖ\s]+$/.test(trimmedLastname)) {
      toastError(getTranslation("lastnameCannotContainSpecialCharacters"));
      return;
    }
    const updateData = { lastname: trimmedLastname };
    setLastname(trimmedLastname);
    Services.UpdateUserData(useToken, userId, updateData)
      .then((res) => {
        if (res.data.status) {
          toastSuccess(getTranslation("successMessage"));
          let userData;
          if (sessionStorage.getItem("user")) {
            userData = JSON.parse(sessionStorage.getItem("user"));
            userData.lastname = trimmedLastname;
            sessionStorage.setItem("user", JSON.stringify(userData));
          } else if (localStorage.getItem("user")) {
            userData = JSON.parse(localStorage.getItem("user"));
            userData.lastname = trimmedLastname;
            localStorage.setItem("user", JSON.stringify(userData));
          }
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch(() => {
        toastError(getTranslation("somethingWentWrong"));
      });
  };

  const handleSaveEmail = (newEmail) => {
    const trimmedEmail = newEmail.trim();
    if (!trimmedEmail) {
      toastError(getTranslation("emailCannotBeEmpty"));
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      toastError(getTranslation("invalidEmailFormat"));
      return;
    }

    if (trimmedEmail === email) {
      return;
    }

    const updateData = { email: trimmedEmail };
    setEmail(trimmedEmail);
    Services.UpdateUserData(useToken, userId, updateData)
      .then((res) => {
        if (res.data.status) {
          toastSuccess(getTranslation("successMessage"));
          let userData;
          if (sessionStorage.getItem("user")) {
            userData = JSON.parse(sessionStorage.getItem("user"));
            userData.email = trimmedEmail;
            sessionStorage.setItem("user", JSON.stringify(userData));
          } else if (localStorage.getItem("user")) {
            userData = JSON.parse(localStorage.getItem("user"));
            userData.email = trimmedEmail;
            localStorage.setItem("user", JSON.stringify(userData));
          }
        } else {
          if (
            Array.isArray(res.data.message) &&
            res.data.message.includes("email already exists")
          ) {
            toastError(getTranslation("emailExist"));
          } else {
            toastError(getTranslation("somethingWentWrong"));
          }
        }
      })
      .catch(() => {
        toastError(getTranslation("somethingWentWrong"));
      });
  };

  useEffect(() => {
    try {
      const sessionUserString = sessionStorage.getItem("user");
      const localUserString = localStorage.getItem("user");

      let userData = null;
      if (sessionUserString) {
        userData = JSON.parse(sessionUserString);
      } else if (localUserString) {
        userData = JSON.parse(localUserString);
      }

      if (userData) {
        setName(userData.name || "");
        setLastname(userData.lastname || "");
        setEmail(userData.email || "");
        if (userData.userAvatar) {
          setAvatar(userData.userAvatar);
        }
      } else {
        setName("User");
        setLastname("");
      }
    } catch (error) {
      console.error("Error parsing user data:", error);
    }
  }, []);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toastError(getTranslation("onlyImageFilesAllowed"));
        return;
      }
      const extension = file.type.split("/")[1];
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(",")[1];
        setAvatar(reader.result);
        const updateData = {
          userAvatar: { file: base64, extension },
        };
        Services.UpdateUserData(useToken, userId, updateData)
          .then((res) => {
            if (res.data.status) {
              toastSuccess(getTranslation("successMessage"));
              let userData;
              if (sessionStorage.getItem("user")) {
                userData = JSON.parse(sessionStorage.getItem("user"));
                userData.userAvatar = res.data.result.userAvatar;
                sessionStorage.setItem("user", JSON.stringify(userData));
              } else if (localStorage.getItem("user")) {
                userData = JSON.parse(localStorage.getItem("user"));
                userData.userAvatar = res.data.result.userAvatar;
                localStorage.setItem("user", JSON.stringify(userData));
              }
            } else {
              toastError(getTranslation("somethingWentWrong"));
            }
          })
          .catch((err) => {
            toastError(getTranslation("somethingWentWrong"));
            console.log(err);
          });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="profile-information-main-content">
      <div className="profile-information-right-content">
        
        <div className="profile-information-title" style={{flexDirection:'row',display:'flex',justifyContent:'space-between'}}>
          {getTranslation("profile")}
            <LanguageSelect />
        </div>
        <div className="profile-information-subtitle" style={{marginTop:'-3px'}}>
          {getTranslation("updateProfileInfo")}
        </div>
        <div className="profile-information-tabs">
          <div className="profile-information-avatar-container">
            <div className="profile-information-avatar-section">
              <span className="profile-information-avatar-section-title">
                {getTranslation("avatar")}
              </span>
              <div
                className="avatar-display"
                onClick={() => {
                  if (fileInputRef.current) {
                    fileInputRef.current.click();
                  }
                }}
                style={{
                  backgroundImage: avatar ? `url(${avatar})` : "none",
                }}
              >
                {!avatar && (
                  <TbUser
                    size={40}
                    style={{
                      margin: "auto",
                      display: "flex",
                      color: "#aaa",
                      alignSelf: "center",
                    }}
                  />
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleAvatarChange}
              />
            </div>
            <div className="profile-information-avatar-upload-section">
              <SettingsButton
                label={getTranslation("uploadNewImage")}
                onClick={() => {
                  if (fileInputRef.current) fileInputRef.current.click();
                }}
              />
              <span className="profile-information-avatar-upload-section-text">
                {getTranslation("recommendedImageSize")}
              </span>
            </div>
          </div>
          <div className="profile-information-profile-input-container">
            <EditableSettingsInput
              label={getTranslation("name")}
              placeholder={getTranslation("name")}
              value={name}
              onSave={handleSaveName}
              icon={TbUser}
              name="name"
            />
            <EditableSettingsInput
              label={getTranslation("lastname")}
              placeholder={getTranslation("lastname")}
              value={lastname}
              onSave={handleSaveLastname}
              icon={TbUser}
              name="lastname"
            />
            <EditableSettingsInput
              label={getTranslation("email")}
              placeholder={getTranslation("email")}
              value={email}
              onSave={handleSaveEmail}
              icon={TbMail}
              name="email"
            />
          </div>

        </div>
      </div>
    </div>
  );
}
